// SelectListDialog.jsx
'use client';
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Radio,
  Divider,
  Tooltip,
  CircularProgress,
  Typography,
  Checkbox,
  FormControlLabel,
  TablePagination,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useLanguage } from '../utils/i18n';

export default function SelectListDialog({
  open,
  onClose,
  title = 'Select',
  options = [],
  getOptionId = (o) => (o == null ? o : (o.id ?? o.value ?? o)),
  getOptionLabel = (o) => (o == null ? '' : (o.label ?? o.name ?? String(o))),
  getOptionSecondaryLabel = (o) => (o == null ? '' : (o.secondaryLabel ?? o.companyName ?? '')),
  getOptionUnit = (o) => (o == null ? '' : (o.unit ?? '')),
  getOptionDisabled = () => false,
  value = null,
  onSelect,
  infoMessage = null,
  allowAdd = false,
  AddComponent = null,
  onAddSuccess = null,
  dialogProps = {},
  onSearch,
  onLoadMore,
  searchKey = 'name',
  searchPlaceholder,
  isLoading = false,
  multiSelect = false,
  pageSize,
  onPageSizeChange,
  pageSizeOptions,
  page,
  onPageChange,
  totalRecord
}) {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(
    multiSelect ? (Array.isArray(value) ? value : value ? [value] : []) : value ? [value] : []
  );
  const { t } = useLanguage();

  const formatSearchKey = (key) => {
    if (!key) return '';
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase())
      .trim();
  };

  const effectivePlaceholder =
    searchPlaceholder ||
    (searchKey === 'all'
      ? 'Search all columns...'
      : searchKey && searchKey !== 'name'
        ? `Search by ${formatSearchKey(searchKey)}...`
        : t('general.searchByName', 'Search by Name...'));
  const [openAdd, setOpenAdd] = useState(false);

  useEffect(() => {
    setSelectedIds(multiSelect ? (Array.isArray(value) ? value : value ? [value] : []) : value ? [value] : []);
  }, [value, multiSelect]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      if (onSearch) onSearch('');
    }
  }, [open, onSearch]);

  const normalized = useMemo(() => {
    return (options || []).map((o) => ({
      id: getOptionId(o),
      label: getOptionLabel(o),
      secondaryLabel: getOptionSecondaryLabel(o),
      unit: getOptionUnit(o),
      disabled: getOptionDisabled(o),
      raw: o
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, getOptionId, getOptionLabel, getOptionSecondaryLabel, getOptionUnit]);

  const filtered = useMemo(() => {
    if (onSearch) return normalized;

    const q = (search || '').trim().toLowerCase();
    if (!q) return normalized;
    return normalized.filter((o) => (o.label || '').toLowerCase().includes(q) || String(o.id).toLowerCase().includes(q));
  }, [normalized, search, onSearch]);

  const handleSearchChange = (e) => {
    const text = e.target.value;
    setSearch(text);
    if (onSearch) {
      onSearch(text);
    }
  };

  const handleConfirm = () => {
    if (multiSelect) {
      onSelect && onSelect(selectedIds);
    } else {
      onSelect && onSelect(selectedIds.length > 0 ? selectedIds[0] : null);
    }
    onClose && onClose();
  };

  const handleClear = () => {
    onSelect && onSelect(multiSelect ? [] : null);
    onClose && onClose();
  };

  const handleScroll = (e) => {
    if (!onLoadMore) return;
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 20) {
      onLoadMore();
    }
  };

  const toggleSelection = (id) => {
    if (multiSelect) {
      setSelectedIds((prev) => {
        const strId = String(id);
        const exists = prev.find((pid) => String(pid) === strId);
        if (exists) return prev.filter((pid) => String(pid) !== strId);
        return [...prev, id];
      });
    } else {
      setSelectedIds([id]);
    }
  };

  const [isSelectingAll, setIsSelectingAll] = useState(false);

  useEffect(() => {
    if (isSelectingAll && !isLoading) {
      if (filtered.length > 0) {
        setSelectedIds(filtered.map((opt) => opt.id));
        setIsSelectingAll(false);
      }
    }
  }, [isLoading, isSelectingAll, filtered]);

  const handleSelectAll = () => {
    if (selectedIds.length === filtered.length && filtered.length > 0) {
      setSelectedIds([]);
    } else {
      if (totalRecord && filtered.length < totalRecord && onPageSizeChange) {
        onPageSizeChange(totalRecord > 0 ? totalRecord : 10000);
        setIsSelectingAll(true);
      } else {
        setSelectedIds(filtered.map((opt) => opt.id));
      }
    }
  };

  const outerFullWidth = dialogProps.fullWidth ?? true;
  const outerMaxWidth = dialogProps.maxWidth ?? 'sm';
  const outerFullScreen = dialogProps.fullScreen ?? false;

  return (
    <>
      <Dialog open={!!open} onClose={onClose} fullWidth={outerFullWidth} maxWidth={outerMaxWidth} fullScreen={outerFullScreen}>
        <DialogTitle>{title}</DialogTitle>

        <DialogContent dividers>
          {infoMessage && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 1 }}>
              {infoMessage}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              placeholder={effectivePlaceholder}
              value={search}
              onChange={handleSearchChange}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: isLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} color="inherit" />
                  </InputAdornment>
                ) : null
              }}
            />
            {allowAdd && AddComponent && (
              <Tooltip title="Add new">
                <IconButton color="primary" onClick={() => setOpenAdd(true)}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {multiSelect && filtered.length > 0 && (
            <Box sx={{ px: 2, pb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedIds.length > 0 && selectedIds.length === filtered.length}
                    indeterminate={selectedIds.length > 0 && selectedIds.length < filtered.length}
                    onChange={handleSelectAll}
                  />
                }
                label={t('dataTable.selectAll', 'Select All')}
              />
            </Box>
          )}

          <List sx={{ maxHeight: 360, overflow: 'auto', p: 0 }} onScroll={handleScroll}>
            {filtered.length === 0 && (
              <Box sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>{isLoading ? 'Mencari data...' : 'No results'}</Box>
            )}
            {filtered.map((opt) => {
              const isSelected = selectedIds.some((id) => String(id) === String(opt.id));
              return (
                <div key={String(opt.id)}>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => {
                      if (!opt.disabled) toggleSelection(opt.id);
                    }}
                    disabled={!!opt.disabled}
                  >
                    <ListItemText
                      primary={opt.label}
                      secondary={opt.secondaryLabel}
                      primaryTypographyProps={{ fontWeight: 500 }}
                      secondaryTypographyProps={opt.secondaryLabel ? { variant: 'caption', color: 'text.secondary' } : undefined}
                    />

                    {opt.unit && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', mr: 2 }}>
                        {opt.unit}
                      </Typography>
                    )}
                    {opt.disabled && typeof opt.disabled === 'string' && (
                      <Typography variant="caption" sx={{ color: 'error.main', mr: 2, fontWeight: 'bold' }}>
                        {opt.disabled}
                      </Typography>
                    )}
                    {multiSelect ? (
                      <Checkbox checked={isSelected} disableRipple disabled={!!opt.disabled} />
                    ) : (
                      <Radio checked={isSelected} disableRipple disabled={!!opt.disabled} />
                    )}
                  </ListItemButton>
                  <Divider />
                </div>
              );
            })}
          </List>
          {onPageChange && (
            <TablePagination
              component="div"
              count={totalRecord || 0}
              page={page || 0}
              onPageChange={(e, newPage) => onPageChange(newPage)}
              rowsPerPage={pageSize || 10}
              onRowsPerPageChange={(e) => onPageSizeChange && onPageSizeChange(parseInt(e.target.value, 10))}
              rowsPerPageOptions={pageSizeOptions || [10, 25, 50, 100]}
              labelRowsPerPage="Per page:"
              sx={{ borderTop: 1, borderColor: 'divider' }}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClear}>{t('buttonTitle.clear', 'Clear')}</Button>
          <Button onClick={onClose}>{t('buttonTitle.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleConfirm} disabled={!multiSelect && selectedIds.length === 0}>
            {t('general.select', t('select', 'Select'))}
          </Button>
        </DialogActions>
      </Dialog>

      {allowAdd && AddComponent && (
        <AddComponent
          open={openAdd}
          id={null}
          onClose={() => setOpenAdd(false)}
          onSuccess={(createdOrMsg) => {
            if (onAddSuccess) onAddSuccess(createdOrMsg);
            setOpenAdd(false);
          }}
        />
      )}
    </>
  );
}

SelectListDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  options: PropTypes.array,
  getOptionId: PropTypes.func,
  getOptionLabel: PropTypes.func,
  getOptionUnit: PropTypes.func,
  value: PropTypes.any,
  onSelect: PropTypes.func,
  infoMessage: PropTypes.node,
  allowAdd: PropTypes.bool,
  AddComponent: PropTypes.any,
  onAddSuccess: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  dialogProps: PropTypes.object,
  onSearch: PropTypes.func,
  onLoadMore: PropTypes.func,
  isLoading: PropTypes.bool,
  multiSelect: PropTypes.bool,
  pageSize: PropTypes.number,
  onPageSizeChange: PropTypes.func,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  page: PropTypes.number,
  onPageChange: PropTypes.func,
  totalRecord: PropTypes.number
};
